import React from 'react';
import { Space } from 'antd';

import { Button } from '../Button';
import { useUIColors } from '../../theme';
import { useUIText } from '../../i18n';
import { renderFilterFieldControl } from './filterFieldControl';
import type { FilterField, FilterValues } from './DataTable.types';

export interface FilterFieldsListProps {
    fields: FilterField[];
    values: FilterValues;
    onChange?: (key: string, value: unknown) => void;
    onReset?: () => void;
    width?: number;
}

/**
 * EAA-1666: shared filter-field renderer extracted from `TableToolbar`
 * (the superset implementation — `dateRange` + `showSearch` select filtering
 * — vs `ListFilters`' subset), so both sit on one implementation instead of
 * two independently-drifting copies. The per-field-type rendering itself now
 * lives in `renderFilterFieldControl` (`filterFieldControl.tsx`), reused by
 * the new column-driven header/toolbar filter UI as well.
 */
export const FilterFieldsList: React.FC<FilterFieldsListProps> = ({
    fields,
    values,
    onChange,
    onReset,
    width = 280,
}) => {
    const colors = useUIColors();
    const { common } = useUIText();

    const renderField = (field: FilterField) =>
        renderFilterFieldControl(
            field,
            values[field.key],
            (value) => onChange?.(field.key, value),
            {
                all: common.all,
                startDate: common.startDate,
                endDate: common.endDate,
            }
        );

    return (
        <Space className="filter-fields-list" direction="vertical" style={{ width }}>
            {fields.map((field) => (
                <div className="filter-fields-list-field" key={field.key}>
                    <div
                        className="filter-fields-list-label"
                        style={{ marginBottom: 4, fontWeight: 500, color: colors.textMuted }}
                    >
                        {field.label}
                    </div>
                    {renderField(field)}
                </div>
            ))}
            {fields.length > 0 && onReset && (
                <Button
                    className="filter-fields-list-reset"
                    type="link"
                    onClick={onReset}
                    style={{ padding: 0 }}
                >
                    {common.resetFilters}
                </Button>
            )}
            {fields.length === 0 && (
                <div style={{ color: colors.textMuted, textAlign: 'center', padding: 16 }}>
                    {common.noFiltersAvailable}
                </div>
            )}
        </Space>
    );
};
